import { formatNumber } from '@rhino-project/core/i18n/format';
import { translate } from '@rhino-project/core/i18n';
export type PointSystem = '2pt' | '3pt' | 'custom';

export interface Standing {
  points: number;
  percentage: number; // 0..1
  percentageLabel: string;
  totalWins: number;
  totalLosses: number;
  gamesPlayed: number;
}

export interface RecordEntry {
  key: string;
  label: string;
  value: number;
}

const POINT_SYSTEMS: readonly PointSystem[] = ['2pt', '3pt', 'custom'];

const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const resolvePointSystem = (value: unknown): PointSystem =>
  POINT_SYSTEMS.includes(value as PointSystem)
    ? (value as PointSystem)
    : 'custom';

export const formatPercent = (percentage: number): string =>
  formatNumber(percentage, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });

// The record display shows every outcome the point system tracks, one entry per
// stored field, ordered like the stats-page inputs: win types, loss types, ties.
// 2pt keeps its single Wins entry (any stored OT/SO wins still count in it, so
// switching systems never drops games); 3pt and custom break the wins out.
export function recordEntries(
  stats: Record<string, unknown> | null | undefined
): RecordEntry[] {
  const s = stats ?? {};
  const system = resolvePointSystem(s.point_system);

  const regWins = num(s.wins);
  const otWins = num(s.overtime_wins);
  const soWins = num(s.shootout_wins);
  const losses = num(s.losses);
  const ties = num(s.ties);
  const otLosses = num(s.overtime_losses);
  const soLosses = num(s.shootout_losses);

  if (system === '2pt') {
    return [
      {
        key: 'wins',
        label: translate('frontend.messages.wins_short'),
        value: regWins + otWins + soWins
      },
      {
        key: 'losses',
        label: translate('frontend.messages.losses_short'),
        value: losses
      },
      {
        key: 'overtime_losses',
        label: translate('frontend.messages.ot_losses_short'),
        value: otLosses
      },
      {
        key: 'shootout_losses',
        label: translate('frontend.messages.so_losses_short'),
        value: soLosses
      },
      {
        key: 'ties',
        label: translate('frontend.messages.ties_short'),
        value: ties
      }
    ];
  }

  return [
    {
      key: 'wins',
      label: translate('frontend.messages.wins_short'),
      value: regWins
    },
    {
      key: 'overtime_wins',
      label: translate('frontend.messages.ot_wins_short'),
      value: otWins
    },
    {
      key: 'shootout_wins',
      label: translate('frontend.messages.so_wins_short'),
      value: soWins
    },
    {
      key: 'losses',
      label: translate('frontend.messages.losses_short'),
      value: losses
    },
    {
      key: 'overtime_losses',
      label: translate('frontend.messages.ot_losses_short'),
      value: otLosses
    },
    {
      key: 'shootout_losses',
      label: translate('frontend.messages.so_losses_short'),
      value: soLosses
    },
    {
      key: 'ties',
      label: translate('frontend.messages.ties_short'),
      value: ties
    }
  ];
}

export function computeStanding(
  stats: Record<string, unknown> | null | undefined
): Standing {
  const s = stats ?? {};
  const system = resolvePointSystem(s.point_system);

  // `wins` holds regulation wins; OT/SO wins are separate, additive counts.
  // Total wins is their sum, so the three win categories can never conflict.
  const regWins = num(s.wins);
  const otWins = num(s.overtime_wins);
  const soWins = num(s.shootout_wins);
  const losses = num(s.losses);
  const ties = num(s.ties);
  const otLosses = num(s.overtime_losses);
  const soLosses = num(s.shootout_losses);

  const totalWins = regWins + otWins + soWins;
  // The record string sums every loss type into "L", mirroring how totalWins
  // sums every win type into "W".
  const totalLosses = losses + otLosses + soLosses;
  const gamesPlayed = totalWins + losses + ties + otLosses + soLosses;

  if (system === 'custom') {
    // In custom the coach enters the win percentage directly (`win_pct`, 0-100);
    // absent it, fall back to the computed win rate so legacy seasons are unchanged.
    const hasManualPct =
      s.win_pct !== undefined && s.win_pct !== null && s.win_pct !== '';
    return {
      points: num(s.points),
      percentage: hasManualPct
        ? num(s.win_pct) / 100
        : gamesPlayed > 0
          ? totalWins / gamesPlayed
          : 0,
      percentageLabel: translate('frontend.messages.win'),
      totalWins,
      totalLosses,
      gamesPlayed
    };
  }

  let points: number;
  let maxPerGame: number;

  if (system === '2pt') {
    points = 2 * totalWins + ties + otLosses + soLosses;
    maxPerGame = 2;
  } else {
    points = 3 * regWins + 2 * (otWins + soWins) + ties + otLosses + soLosses;
    maxPerGame = 3;
  }

  return {
    points,
    percentage: gamesPlayed > 0 ? points / (maxPerGame * gamesPlayed) : 0,
    percentageLabel: translate('frontend.messages.points_2'),
    totalWins,
    totalLosses,
    gamesPlayed
  };
}
